<template>
  <v-card
    elevation="0"
    class="modern-needs-card rounded-3xl overflow-hidden"
    :class="{ 'dark-mode': isDarkMode }"
  >
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="32" class="icon-needs">mdi-heart-outline</v-icon>
        </div>
        <div class="header-text">
          <h1 class="header-title">Community Needs</h1>
          <p class="header-subtitle">Track and manage community assistance requests</p>
        </div>
        <div class="header-actions">
          <v-btn
            icon
            size="small"
            variant="text"
            class="action-btn"
            @click="refreshData"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            icon
            size="small"
            variant="text"
            class="theme-toggle"
            @click="toggleTheme"
          >
            <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-section">
      <div class="content-grid">
        <!-- Stats Panel -->
        <div class="stats-panel">
          <div class="stats-header">
            <h3 class="stats-title">Quick Stats</h3>
            <div class="stats-indicator">
              <div class="pulse-dot"></div>
              <span class="live-text">Live</span>
            </div>
          </div>
          
          <div class="stats-list">
            <div
              v-for="(stat, index) in statsData"
              :key="stat.type"
              class="stat-item"
              :class="{ 'animate-in': animateStats }"
              :style="{ '--delay': `${index * 0.15}s` }"
              @click="goToNeeds(stat.route)"
            >
              <div class="stat-visual">
                <div class="stat-icon" :style="{ backgroundColor: stat.color + '20' }">
                  <v-icon :color="stat.color" size="20">{{ stat.icon }}</v-icon>
                </div>
                <div class="stat-progress">
                  <div 
                    class="stat-progress-bar"
                    :style="{ 
                      width: `${stat.percentage}%`,
                      backgroundColor: stat.color
                    }"
                  ></div>
                </div>
              </div>
              <div class="stat-content">
                <div class="stat-number">
                  {{ animatedValues[stat.type] || 0 }}
                </div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
              <div class="stat-trend" v-if="stat.trend !== undefined">
                <v-icon
                  :color="stat.trend >= 0 ? 'success' : 'error'"
                  size="14"
                >
                  {{ stat.trend >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
                </v-icon>
                <!-- <span class="trend-value">{{ Math.abs(stat.trend) }}%</span> -->
              </div>
            </div>
          </div>
        </div>

        <!-- Chart Panel -->
        <div class="chart-panel">
          <div class="chart-header">
            <h3 class="chart-title">Month Based Active Needs</h3>
            <div class="chart-controls">
              <v-btn-toggle
                v-model="selectedChartType"
                mandatory
                variant="outlined"
                size="small"
                class="chart-toggle"
              >
                <v-btn value="column" size="small">
                  <v-icon size="16">mdi-chart-bar</v-icon>
                </v-btn>
                <v-btn value="line" size="small">
                  <v-icon size="16">mdi-chart-line</v-icon>
                </v-btn>
                <v-btn value="area" size="small">
                  <v-icon size="16">mdi-chart-areaspline</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>
          
          <div class="chart-container">
            <div class="chart-wrapper">
              <highcharts
                :options="chartOptions"
                class="modern-chart"
              ></highcharts>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      isDarkMode: false,
      selectedChartType: 'column',
      selectedPeriod: 0,
      animateStats: false,
      animatedValues: {},
      timePeriods: [
        { label: '7D', value: '7d' },
        { label: '30D', value: '30d' },
        { label: '3M', value: '3m' },
        { label: '1Y', value: '1y' }
      ],
      chartOptions: {
        chart: {
          type: "column",
          backgroundColor: 'transparent',
          borderRadius: 16,
          marginBottom: 40,
          marginLeft: 60,
          marginRight: 30,
          marginTop: 40,
          style: {
            fontFamily: 'Inter, system-ui, sans-serif'
          }
        },
        credits: {
          enabled: false
        },
        title: {
          text: null
        },
        xAxis: {
          type: "datetime",
          gridLineWidth: 0,
          lineWidth: 0,
          tickWidth: 0,
          labels: {
            style: {
              color: '#64748b',
              fontSize: '12px'
            },
            formatter: function() {
              return moment(this.value).format("MMM DD");
            }
          }
        },
        yAxis: {
          title: { text: null },
          gridLineWidth: 1,
          gridLineColor: '#f1f5f9',
          labels: {
            style: {
              color: '#64748b',
              fontSize: '12px'
            }
          }
        },
        plotOptions: {
          column: {
            borderRadius: 6,
            pointPadding: 0.1,
            groupPadding: 0.15,
            color: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, '#7024C4'],
                [1, '#9333ea']
              ]
            }
          },
          line: {
            marker: {
              enabled: true,
              radius: 4
            },
            lineWidth: 3
          },
          area: {
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, 'rgb(17, 179, 216)'],
                [1, 'rgba(17, 179, 216, 0.05)']
              ]
            },
            lineWidth: 3
          }
        },
        series: [
          {
            showInLegend: false,
            data: this.needGraphDatas,
            color: "#11b3d8",
            marker: {
              enabled: true
            }
          }
        ],
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: 'transparent',
          borderRadius: 8,
          style: {
            color: '#ffffff',
            fontSize: '13px'
          },
          formatter: function() {
            return `<b>${moment(this.x).format("MMM DD, YYYY")}</b><br/>
                    Needs: <b>${this.y}</b>`;
          }
        },
        legend: {
          enabled: false
        }
      }
    };
  },
  computed: {
    ...mapGetters("adminDashboard", ["getNeedsCount", "needGraphData"]),
    needGraphDatas() {
      const data = this.needGraphData || [];
      return data;
    },
    statsData() {
      const total = this.getNeedsCount?.totalNeeds || 0;
      const active = this.getNeedsCount?.activeNeeds || 0;
      const completed = total - active; // Calculate completed as total minus active
      const agenciesParticipated = this.getNeedsCount?.participatedAgencies || 0;
      
      
      return [
        {
          type: 'Total',
          label: 'Total Needs',
          value: total,
          icon: 'mdi-heart-outline',
          color: '#7024C4',
          route: 'all',
          trend: 5.2,
          percentage: 100
        },
        {
          type: 'Active',
          label: 'Active',
          value: active,
          icon: 'mdi-clock-outline',
          color: '#f59e0b',
          route: 'upcoming',
          trend: 8.7,
          percentage: total > 0 ? (active / total) * 100 : 0
        },
        {
          type: 'Completed',
          label: 'Completed',
          value: completed,
          icon: 'mdi-check-circle',
          color: '#10b981',
          route: 'completed',
          trend: 12.3,
          percentage: total > 0 ? (completed / total) * 100 : 0
        },
        {
          type: 'AgenciesParticipated',
          label: 'Agencies Participated',
          value: agenciesParticipated,
          icon: 'mdi-office-building',
          color: '#3b82f6',
          route: 'agencies-participated',
          trend: 15.8,
          percentage: total > 0 ? (agenciesParticipated / total) * 100 : 0
        }
      ];
    }
  },
  methods: {
    ...mapActions("adminDashboard", [
      "fetchNeedCountDetails",
      "fetchNeedGraphDetails"
    ]),
    goToNeeds(type) {
      // If clicking on Agencies Participated, go to agencies page with active filter
      if (type === 'agencies-participated') {
        this.$router.push({ 
          name: "admin-agencies",
          query: { activeAgencies: 'yes' }
        });
      } else {
        this.$router.push({ name: "agency-need-feeds", query: { type } });
      }
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
    },
    refreshData() {
      this.fetchNeedCountDetails();
      this.fetchNeedGraphDetails();
    },
    animateNumbers() {
      this.statsData.forEach(stat => {
        this.animateCounter(stat.type, stat.value);
      });
    },
    animateCounter(type, target) {
      const duration = 1200;
      const start = Date.now();
      const startValue = 0;
      
      const animate = () => {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.floor(startValue + (target - startValue) * eased);
        
        this.$set(this.animatedValues, type, current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      animate();
    }
  },
  watch: {
    needGraphData(newData) {
      this.chartOptions.series[0].data = newData;
    },
    selectedChartType(newType) {
      this.chartOptions.chart.type = newType;
    },
    getNeedsCount: {
      handler() {
        this.$nextTick(() => {
          this.animateNumbers();
        });
      },
      deep: true
    }
  },
  mounted() {
    this.fetchNeedCountDetails();
    this.fetchNeedGraphDetails();
    
    setTimeout(() => {
      this.animateStats = true;
      this.animateNumbers();
    }, 300);
  }
};
</script>

<style scoped>
.modern-needs-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  max-width: 100%;
  margin: 0;
  border-radius: 20px;
}

.modern-needs-card.dark-mode {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: rgba(255, 255, 255, 0.1);
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #00aeff  0%, #22b7b0 100%);
  padding: 1rem;
  position: relative;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.75rem;
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.icon-needs {
  color: white !important;
}

.header-text {
  flex: 1;
  min-width: 200px;
}

.header-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  margin: 0.25rem 0 0 0;
  line-height: 1.4;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.action-btn, .theme-toggle {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  color: white !important;
}

/* Content Section */
.content-section {
  padding: 1rem;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(250px, 300px) 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

/* Stats Panel */
.stats-panel {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  height: fit-content;
}

.dark-mode .stats-panel {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.dark-mode .stats-title {
  color: #f1f5f9;
}

.stats-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.pulse-dot {
  width: 0.5rem;
  height: 0.5rem;
  background: #10b981;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.live-text {
  font-size: 0.75rem;
  color: #10b981;
  font-weight: 500;
}

.stats-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  opacity: 0;
  transform: translateY(20px);
}

.stat-item.animate-in {
  animation: slideUp 0.6s ease forwards;
  animation-delay: var(--delay);
}

.dark-mode .stat-item {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.stat-visual {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 40px;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-progress {
  width: 40px;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.stat-progress-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 1s ease;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark-mode .stat-number {
  color: #f1f5f9;
}

.stat-label {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark-mode .stat-label {
  color: #94a3b8;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;
}

/* Chart Panel */
.chart-panel {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
}

.dark-mode .chart-panel {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.chart-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.dark-mode .chart-title {
  color: #f1f5f9;
}

.chart-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.chart-toggle {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 0.75rem;
  overflow: hidden;
}

.chart-container {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
}

.dark-mode .chart-container {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}

.chart-wrapper {
  min-height: 250px;
  height: 100%;
  width: 100%;
}

.modern-chart {
  height: 100%;
  width: 100%;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .header-content {
    gap: 0.75rem;
  }
  
  .header-title {
    font-size: 1.25rem;
  }
  
  .stats-list {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 576px) {
  .header-section {
    padding: 0.75rem;
  }
  
  .header-icon {
    padding: 0.5rem;
  }
  
  .header-text {
    min-width: calc(100% - 120px);
  }
  
  .stats-list {
    grid-template-columns: 1fr;
  }
  
  .content-section {
    padding: 0.75rem;
  }
  
  .stats-panel,
  .chart-panel {
    padding: 0.75rem;
    border-radius: 1rem;
  }
  
  .stat-item {
    padding: 0.75rem;
  }
  
  .chart-container {
    padding: 0.75rem;
    border-radius: 1rem;
  }
}

@media (max-width: 400px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    align-self: flex-end;
    margin-top: 0.5rem;
  }
  
  .header-text {
    min-width: 100%;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>